import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { type AirActionTrackingLocation, useTrackRegeneratedShareLink } from '@air/analytics';
import { ShortUrl } from '@air/api';
import { type ListResponse, type ShortUrlResponse } from '@air/api/types';
import { type CollectionResponse } from '@air/server-sdk';
import { reportErrorToBugsnag } from '@air/utils-error';

import { updateShortUrlInListCache } from '~/components/SharePrivateCollectionModal/hooks/mutations/utils';
import { getShortUrlForCollectionKey } from '~/components/SharePrivateCollectionModal/hooks/queries/useGetShortUrlForCollection';
import { isShortUrlsListKey } from '~/swr-hooks/shortUrl/useListShortUrls';

export const useRegenerateShortIdForCollection = () => {
  const queryClient = useQueryClient();
  const { trackRegeneratedShareLink } = useTrackRegeneratedShareLink();

  const regenerateShortIdForCollection = useMutation({
    mutationFn: ({
      ...rest
    }: Parameters<typeof ShortUrl.regenerateShortIdForCollection>[0] & {
      collectionId: CollectionResponse['id'];
      trackLocation: AirActionTrackingLocation;
    }) => ShortUrl.regenerateShortIdForCollection(rest),
    onSuccess: (shortIdInfo, { collectionId, trackLocation, workspaceId, shortId: oldShortId }) => {
      queryClient.setQueryData(getShortUrlForCollectionKey({ collectionId }), shortIdInfo);

      // Update the list cache with the new short URL data
      if (workspaceId && oldShortId) {
        queryClient.setQueriesData<InfiniteData<ListResponse<ShortUrlResponse>> | undefined>(
          {
            predicate: ({ queryKey }) => isShortUrlsListKey(queryKey) && queryKey[1].workspaceId === workspaceId,
          },
          (old) =>
            old
              ? updateShortUrlInListCache(
                  oldShortId,
                  {
                    workspaceId,
                    shortId: oldShortId,
                    ...shortIdInfo,
                  },
                  old,
                  { newId: shortIdInfo.id },
                )
              : old,
        );
      }

      trackRegeneratedShareLink({ shortId: shortIdInfo.id, location: trackLocation });

      return shortIdInfo;
    },
    onError: (error, params) => {
      throw reportErrorToBugsnag({
        error,
        context: 'Failed to regenerate short id for collection',
        metadata: { data: params },
      });
    },
  });

  return {
    regenerateShortIdForCollection,
  };
};
